import React, { useState, useCallback, useMemo, useId } from "react";
import type { Table, Column } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { TableDensity } from "@/hooks/useTablePreferences";
import { Settings2, ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { Trans } from "@lingui/react";
import { useLingui } from "@lingui/react/macro";

interface ColumnItemProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  column: Column<any>;
  isVisible: boolean;
  onToggle: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst: boolean;
  isLast: boolean;
}

function ColumnItem({
  column,
  isVisible,
  onToggle,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: ColumnItemProps) {
  // Subscribe to language changes to ensure headers update reactively
  const { i18n: linguiReactivity } = useLingui();
  
  // Get column header text - handle empty headers for special columns like avatar
  // This is not memoized to ensure it updates when language changes
  const columnHeader = (() => {
    // For TanStack Table v8, the header can be a string, function, or React component
    const headerDef = column.columnDef.header;
    
    // If it's already a string (like our translated headers), use it directly
    if (typeof headerDef === "string") {
      return headerDef;
    }
    
    // If it's a React element (like <div>{t("Status")}</div>), extract the text
    if (React.isValidElement(headerDef)) {
      // Try to extract text from the React element
      const extractText = (element: React.ReactElement): string => {
        const props = element.props as { children?: React.ReactNode };
        if (typeof props.children === "string") {
          return props.children;
        }
        if (React.isValidElement(props.children)) {
          return extractText(props.children);
        }
        if (Array.isArray(props.children)) {
          return props.children
            .map((child: React.ReactNode) => {
              if (typeof child === "string") return child;
              if (React.isValidElement(child)) return extractText(child);
              return "";
            })
            .join("");
        }
        return "";
      };
      
      const text = extractText(headerDef);
      if (text) return text;
    }
    
    // If header is empty or not a string, handle special cases
    if (!headerDef || (typeof headerDef === "string" && headerDef === "")) {
      // For columns with empty headers, use the column ID but make it more readable
      if (column.id === "avatar") return "Avatar";
      return column.id;
    }
    
    // If it's a function or other type, fall back to column ID
    return column.id;
  })();

  return (
    <div
      className={cn(
        "flex items-center space-x-2 px-2 py-1 rounded-md hover:bg-accent/50 transition-colors cursor-pointer",
        !column.getCanHide() && "cursor-not-allowed opacity-60"
      )}
      onClick={(e) => {
        // Don't toggle if clicking on the up/down buttons
        if ((e.target as HTMLElement).closest("button")) return;
        if (column.getCanHide()) {
          onToggle();
        }
      }}
    >
      <div className="flex flex-col" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="ghost"
          size="sm"
          onClick={onMoveUp}
          disabled={isFirst}
          className="h-5 w-5 p-0"
          aria-label={linguiReactivity._(
            { id: "table.columns.moveUp", message: "Move {column} up" },
            { column: columnHeader }
          )}
        >
          <ChevronUp className="h-3 w-3" aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onMoveDown}
          disabled={isLast}
          className="h-5 w-5 p-0"
          aria-label={linguiReactivity._(
            { id: "table.columns.moveDown", message: "Move {column} down" },
            { column: columnHeader }
          )}
        >
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        </Button>
      </div>
      <Checkbox
        id={`column-${column.id}`}
        checked={isVisible}
        onCheckedChange={onToggle}
        disabled={!column.getCanHide()}
        className="data-[state=checked]:bg-primary data-[state=checked]:border-primary pointer-events-none"
      />
      <div className="flex-1 text-sm select-none">{columnHeader}</div>
      {isVisible ? (
        <Eye className="h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
      ) : (
        <EyeOff className="h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
      )}
    </div>
  );
}

interface TableColumnManagerProps<T> {
  table: Table<T>;
  onReset?: () => void;
  /** Current row density, shown in the density switch. */
  density?: TableDensity;
  /** Stores a new density. The switch renders only when this is given. */
  onDensityChange?: (density: TableDensity) => void;
}

export function TableColumnManager<T>({
  table,
  onReset,
  density = "compact",
  onDensityChange,
}: TableColumnManagerProps<T>) {
  const [open, setOpen] = useState(false);
  const densityLabelId = useId();
  // Track column order changes to force re-render
  const [orderVersion, setOrderVersion] = useState(0);
  
  // Subscribe to language changes to update column headers
  const { i18n } = useLingui();
  const currentLocale = i18n.locale;

  // Get all manageable columns (exclude select, avatar, and searchableContent which are always visible or hidden)
  const columns = useMemo(() => {
    const allColumns = table.getAllColumns();
    // Filter out columns that shouldn't appear in the manage columns UI
    return allColumns.filter(
      (col) => col.id !== "select" && col.id !== "avatar" && col.id !== "searchableContent"
    );
  }, [table]);

  // Get the ordered manageable columns based on current table state
  const orderedColumns = useMemo(() => {
    const currentOrder = table.getState().columnOrder;
    const allColumns = table.getAllColumns();

    // Create a map of all columns for quick lookup
    const columnMap = new Map(allColumns.map((col) => [col.id, col]));

    // If we have a column order, use it (excluding fixed columns)
    if (currentOrder && currentOrder.length > 0) {
      return currentOrder
        .filter((id) => id !== "select" && id !== "avatar" && id !== "searchableContent")
        .map((id) => columnMap.get(id))
        .filter((col): col is Column<T> => col !== undefined);
    }

    // Otherwise, return all manageable columns in their default order
    return columns;
    // orderVersion is intentionally included to force re-computation when column order changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, columns, orderVersion]);

  // Count visible manageable columns (excluding always-visible select and avatar)
  const visibleCount = columns.filter((col) => col.getIsVisible()).length;
  const hideableColumns = columns.filter((col) => col.getCanHide());
  const visibleHideableCount = hideableColumns.filter((col) =>
    col.getIsVisible()
  ).length;
  const allHideableVisible = visibleHideableCount === hideableColumns.length;
  const toggleAllLabel = allHideableVisible
    ? i18n._({ id: "table.columns.hideAll", message: "Hide all columns" })
    : i18n._({ id: "table.columns.showAll", message: "Show all columns" });

  // Toggle all columns based on current state
  const toggleAllColumns = useCallback(() => {
    const newVisibility = { ...table.getState().columnVisibility };

    hideableColumns.forEach((column) => {
      // If all are visible, hide all; otherwise show all
      newVisibility[column.id] = !allHideableVisible;
    });

    table.setColumnVisibility(newVisibility);
  }, [hideableColumns, table, allHideableVisible]);

  // Check if current state matches defaults (for reset button styling)
  const columnOrder = table.getState().columnOrder;
  const isDefaultState = useMemo(() => {
    const currentVisibility = table.getState().columnVisibility;
    const currentOrder = columnOrder;

    // Check if visibility is at defaults (all columns visible)
    const hasVisibilityChanges = Object.keys(currentVisibility).some(
      (columnId) => {
        return currentVisibility[columnId] === false;
      }
    );

    // Check if order is at defaults
    const allColumns = table.getAllColumns().map((col) => col.id);
    const defaultOrder = allColumns;
    const hasOrderChanges =
      currentOrder.length > 0 &&
      JSON.stringify(currentOrder) !== JSON.stringify(defaultOrder);

    return !hasVisibilityChanges && !hasOrderChanges;
  }, [table, columnOrder]);

  // Move column up/down in order
  const moveColumn = useCallback(
    (fromIndex: number, direction: "up" | "down") => {
      // Work with just the manageable columns for the movement
      const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
      if (toIndex < 0 || toIndex >= orderedColumns.length) return;

      // Get the current full column order from the table
      const currentOrder = table.getState().columnOrder;
      const allColumns = table.getAllColumns().map((col) => col.id);

      // If no order is set, create default with fixed columns first
      const fullOrder =
        currentOrder.length > 0 ? [...currentOrder] : allColumns;

      // Ensure fixed columns are always first
      const fixedIds = ["select", "avatar", "searchableContent"];
      const fixedColumns = fixedIds.filter((id) => fullOrder.includes(id));
      const manageableOrder = fullOrder.filter((id) => !fixedIds.includes(id));

      // Get the columns being swapped
      const columnId1 = orderedColumns[fromIndex].id;
      const columnId2 = orderedColumns[toIndex].id;

      // Find their positions in the manageable order
      const idx1 = manageableOrder.indexOf(columnId1);
      const idx2 = manageableOrder.indexOf(columnId2);

      // Swap them
      [manageableOrder[idx1], manageableOrder[idx2]] = [
        manageableOrder[idx2],
        manageableOrder[idx1],
      ];

      // Rebuild full order with fixed columns first
      const newOrder = [...fixedColumns, ...manageableOrder];

      table.setColumnOrder(newOrder);
      // Force re-render to update the displayed order
      setOrderVersion((v) => v + 1);
    },
    [table, orderedColumns]
  );

  // Reset to defaults
  const handleReset = useCallback(() => {
    // Reset visibility - show all columns
    table.resetColumnVisibility();
    // Reset order - use original column order
    table.resetColumnOrder();
    // Force re-render to update the displayed order
    setOrderVersion((v) => v + 1);
    // Call the onReset callback if provided (e.g., to clear localStorage)
    if (onReset) {
      onReset();
    }
  }, [table, onReset]);

  return (
    <Popover key={currentLocale} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="ml-auto h-10">
          <Settings2 className="h-4 w-4 mr-2" />
          <Trans id="table.columns.manage">Columns</Trans>
          <span className="ml-2 text-muted-foreground">
            ({visibleCount}/{columns.length})
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[280px] p-0">
        <div className="p-4">
          <h4 className="font-medium text-sm mb-3">
            <Trans id="table.columns.title">Manage Columns</Trans>
          </h4>

          <div className="flex items-center space-x-2 mb-3 pr-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className={cn(
                "h-8 px-2 text-xs",
                isDefaultState ? "text-muted-foreground" : "font-semibold"
              )}
            >
              <Trans id="table.columns.reset">Reset</Trans>
            </Button>
            <div className="flex-1"></div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={toggleAllColumns}
              className="h-auto px-2 py-1"
              aria-label={toggleAllLabel}
              title={toggleAllLabel}
            >
              {allHideableVisible ? (
                <Eye className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              ) : (
                <EyeOff className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              )}
            </Button>
          </div>

          <Separator className="mb-3" />

          {onDensityChange && (
            <>
              <div className="mb-3 flex items-center justify-between gap-2 pr-3">
                <span id={densityLabelId} className="text-sm text-muted-foreground">
                  {i18n._({ id: "table.density.label", message: "Row height" })}
                </span>
                <ToggleGroup
                  type="single"
                  size="sm"
                  variant="outline"
                  value={density}
                  aria-labelledby={densityLabelId}
                  onValueChange={(value) => {
                    // Radix reports "" when the pressed item is clicked again.
                    if (value === "compact" || value === "comfortable") {
                      onDensityChange(value);
                    }
                  }}
                >
                  <ToggleGroupItem value="compact" className="h-8 px-2 text-xs">
                    {i18n._({ id: "table.density.compact", message: "Compact" })}
                  </ToggleGroupItem>
                  <ToggleGroupItem value="comfortable" className="h-8 px-2 text-xs">
                    {i18n._({ id: "table.density.comfortable", message: "Comfortable" })}
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
              <Separator className="mb-3" />
            </>
          )}

          <ScrollArea className="max-h-[calc(100vh-12rem)] pr-3">
            <div className="space-y-0.5">
              {orderedColumns.map((column, index) => (
                <ColumnItem
                  key={column.id}
                  column={column}
                  isVisible={column.getIsVisible()}
                  onToggle={() => {
                    if (column.getCanHide()) {
                      column.toggleVisibility();
                    }
                  }}
                  onMoveUp={() => moveColumn(index, "up")}
                  onMoveDown={() => moveColumn(index, "down")}
                  isFirst={index === 0}
                  isLast={index === orderedColumns.length - 1}
                />
              ))}
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
}
